import { createContext, ReactNode, useContext, useState } from "react";
import { Plus } from "lucide-react";

/**
 * What a screen lets an author add to one of its document's option lists, from the field
 * that picks in it.
 *
 * A field choosing from the document (`document_options`) may also declare
 * `document_create: "<name>"`: the author can add the missing entry where they noticed it
 * was missing — « New variable… » under the variable a write targets — instead of leaving
 * the form for the drawer that owns the list. The field only names the creation; the screen
 * that owns the document registers what it does, because only that screen knows how its
 * document is written (a draft operation, a fork…). A name nobody registered offers nothing,
 * which is what a read-only screen wants.
 */
export type DocumentCreator = {
    /** What the button says, e.g. « New variable… ». */
    label: string;
    /**
     * The inline form. `onCreated` hands back the value the field should now hold, with the
     * label and facts the option list will carry once the document has answered.
     */
    render: (props: { onCreated: (option: CreatedOption) => void; onCancel: () => void }) => ReactNode;
};

/** An option created from a field, held by that field until the document lists it. */
export type CreatedOption = { value: string; label: string } & Record<string, unknown>;

const DocumentCreatorsContext = createContext<Record<string, DocumentCreator>>({});

export function DocumentCreatorsProvider({ creators, children }: { creators: Record<string, DocumentCreator>; children: ReactNode }) {
    return <DocumentCreatorsContext.Provider value={creators}>{children}</DocumentCreatorsContext.Provider>;
}

export function useDocumentCreator(name: string | null | undefined): DocumentCreator | undefined {
    const creators = useContext(DocumentCreatorsContext);
    return name ? creators[name] : undefined;
}

/** The button and, once pressed, the creator's inline form. */
export function DocumentCreateAffordance({ id, creator, onCreated }: { id: string; creator: DocumentCreator; onCreated: (option: CreatedOption) => void }) {
    const [open, setOpen] = useState(false);

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid={`document-create-${id}`}
                className="flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
                <Plus className="h-3 w-3" />
                {creator.label}
            </button>
        );
    }

    return (
        <div className="rounded-sm border bg-gray-50 p-2" data-testid={`document-create-form-${id}`}>
            {creator.render({
                onCreated: option => {
                    setOpen(false);
                    onCreated(option);
                },
                onCancel: () => setOpen(false)
            })}
        </div>
    );
}
